import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  PlanTier,
  getPlanLimits,
  canUseFeature,
  checkUsageLimit,
  getUpgradeMessage,
  PlanLimits,
  UsageStats,
} from "@/lib/plan-limits";

interface UsePlanLimitsReturn {
  planTier: PlanTier;
  limits: PlanLimits;
  usage: UsageStats | null;
  loading: boolean;
  canUse: (feature: keyof PlanLimits['features']) => boolean;
  checkLimit: (action: 'ai_call' | 'upload' | 'run' | 'seat') => { allowed: boolean; reason?: string };
  incrementUsage: (type: 'ai_call' | 'upload') => Promise<void>;
  showUpgradePrompt: (feature: string) => void;
  refetchUsage: () => Promise<void>;
}

export function usePlanLimits(organizationId: string | null): UsePlanLimitsReturn {
  const [planTier, setPlanTier] = useState<PlanTier>('free');
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPlanAndUsage = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      // Fetch organization plan
      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .select("plan_tier, plan_seat_limit")
        .eq("id", organizationId)
        .single();

      if (orgError) throw orgError;
      
      setPlanTier((org.plan_tier as PlanTier) || 'free');

      // Fetch current usage stats
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const startOfHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()).toISOString();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      // Fetch AI calls today and this hour
      const { count: aiToday } = await supabase
        .from("agent_audit_log")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("function_category", "ai")
        .gte("created_at", startOfDay);

      const { count: aiThisHour } = await supabase
        .from("agent_audit_log")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("function_category", "ai")
        .gte("created_at", startOfHour);

      // Fetch runs this month
      const { count: runsThisMonth } = await supabase
        .from("sop_runs")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .gte("created_at", startOfMonth);

      // Fetch seats used
      const { count: seatsUsed } = await supabase
        .from("memberships")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", organizationId);

      // Fetch uploads today
      const { count: uploadsToday } = await supabase
        .from("proof_attachments")
        .select("*", { count: "exact", head: true })
        .gte("created_at", startOfDay);

      setUsage({
        aiCallsToday: aiToday || 0,
        aiCallsThisHour: aiThisHour || 0,
        uploadsToday: uploadsToday || 0,
        runsThisMonth: runsThisMonth || 0,
        storageUsedGB: 0, // Would need storage API call
        seatsUsed: seatsUsed || 0,
      });
    } catch (error) {
      console.error("Failed to fetch plan limits:", error);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchPlanAndUsage();
  }, [fetchPlanAndUsage]);

  const canUse = useCallback(
    (feature: keyof PlanLimits['features']): boolean => {
      return canUseFeature(planTier, feature);
    },
    [planTier]
  );

  const checkLimit = useCallback(
    (action: 'ai_call' | 'upload' | 'run' | 'seat') => {
      if (!usage) return { allowed: true };
      return checkUsageLimit(planTier, usage, action);
    },
    [planTier, usage]
  );

  const incrementUsage = useCallback(
    async (type: 'ai_call' | 'upload') => {
      if (!usage) return;

      // Optimistically update local state
      setUsage((prev) => {
        if (!prev) return prev;
        switch (type) {
          case 'ai_call':
            return {
              ...prev,
              aiCallsToday: prev.aiCallsToday + 1,
              aiCallsThisHour: prev.aiCallsThisHour + 1,
            };
          case 'upload':
            return {
              ...prev,
              uploadsToday: prev.uploadsToday + 1,
            };
          default:
            return prev;
        }
      });
    },
    [usage]
  );

  const showUpgradePrompt = useCallback(
    (feature: string) => {
      const message = getUpgradeMessage(planTier, feature);
      toast({
        title: "Upgrade Required",
        description: message,
        action: (
          <a href="/pricing" className="underline font-medium">
            View Plans
          </a>
        ),
      });
    },
    [planTier, toast]
  );

  return {
    planTier,
    limits: getPlanLimits(planTier),
    usage,
    loading,
    canUse,
    checkLimit,
    incrementUsage,
    showUpgradePrompt,
    refetchUsage: fetchPlanAndUsage,
  };
}
